<template>
  <div class="img-evento-page container py-4">
    <div class="text-center mb-4">
      <h1 class="h3 font-weight-bold">
        AMII es IA
      </h1>
      <p class="text-muted">
        Llévate una imagen tuya generada con inteligencia artificial
      </p>
    </div>

    <!-- PASO 1: CÓDIGOS DEL EVENTO -->
    <div
      v-if="step === 'codes'"
      class="card shadow-sm p-4 mx-auto"
      style="max-width: 480px; border-radius: 12px;"
    >
      <h2 class="h5 font-weight-bold mb-3">
        Acceso del evento
      </h2>
      <div class="form-group mb-3">
        <label class="font-weight-bold small">Código del evento</label>
        <input
          v-model="eventCode"
          type="text"
          class="form-control"
          placeholder="Ej. NEUMO2026"
        >
      </div>
      <div class="form-group mb-3">
        <label class="font-weight-bold small">Código de autorización</label>
        <input
          v-model="authCode"
          type="password"
          class="form-control"
          placeholder="Código entregado por AMII"
        >
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger small"
      >
        {{ errorMessage }}
      </div>

      <button
        class="btn btn-primary btn-block font-weight-bold py-2"
        style="border-radius: 20px;"
        :disabled="validating"
        @click="validateCodes"
      >
        {{ validating ? 'Validando...' : 'Comenzar' }}
      </button>
    </div>

    <!-- PASO 2: ESPECIALIDAD + CÁMARA -->
    <div
      v-else-if="step === 'capture'"
      class="card shadow-sm p-4 mx-auto"
      style="max-width: 560px; border-radius: 12px;"
    >
      <h2 class="h5 font-weight-bold mb-3">
        {{ eventName }}
      </h2>

      <div class="form-group mb-3">
        <label class="font-weight-bold small">Tu especialidad</label>
        <select
          v-model="selectedSpecialty"
          class="form-control"
        >
          <option
            :value="null"
            disabled
          >
            Selecciona tu especialidad
          </option>
          <option
            v-for="sp in specialties"
            :key="sp"
            :value="sp"
          >
            {{ sp }}
          </option>
        </select>
      </div>

      <div class="form-group mb-3">
        <label class="font-weight-bold small">
          Nombre o texto personalizado (opcional)
        </label>
        <input
          v-model="extraInfo"
          type="text"
          class="form-control"
          placeholder="Ej. Dra. Pérez / Especialista invitado"
          maxlength="100"
        >
      </div>

      <div class="text-center mb-3">
        <video
          ref="video"
          autoplay
          playsinline
          class="w-100 rounded"
          style="max-height: 360px; background: #000;"
        />
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger small"
      >
        {{ errorMessage }}
      </div>

      <button
        class="btn btn-primary btn-block font-weight-bold py-2"
        style="border-radius: 20px;"
        :disabled="!selectedSpecialty || capturing"
        @click="capturePhoto"
      >
        {{ capturing ? 'Generando...' : 'Tomar foto y generar' }}
      </button>
    </div>

    <!-- PASO 3: ESPERA -->
    <div
      v-else-if="step === 'waiting'"
      class="card shadow-sm p-5 text-center mx-auto"
      style="max-width: 480px; border-radius: 12px;"
    >
      <div class="spinner-border text-primary mb-3" />
      <h2 class="h5 font-weight-bold">
        Generando tu imagen...
      </h2>
      <p class="text-muted small mb-0">
        Esto puede tardar unos segundos. No cierres esta pantalla.
      </p>
    </div>

    <!-- PASO 4: RESULTADO + QR -->
    <div
      v-else-if="step === 'result'"
      class="card shadow-sm p-4 mx-auto"
      style="max-width: 560px; border-radius: 12px;"
    >
      <h2 class="h5 font-weight-bold mb-3 text-center">
        ¡Tu imagen está lista!
      </h2>

      <div class="text-center mb-3">
        <img
          :src="resultImageUrl"
          alt="Imagen generada"
          class="img-fluid rounded"
          style="max-height: 420px;"
        >
      </div>

      <div class="text-center mb-3">
        <p class="small text-muted mb-2">
          Escanea el QR para verla en tu teléfono
        </p>
        <img
          :src="qrDataUrl"
          alt="QR"
          style="width: 180px; height: 180px;"
        >
      </div>

      <a
        :href="whatsappShareUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="btn btn-success btn-block font-weight-bold py-2 mb-2"
        style="border-radius: 20px;"
      >
        Compartir por WhatsApp
      </a>

      <button
        class="btn btn-outline-primary btn-block font-weight-bold py-2"
        style="border-radius: 20px;"
        @click="startOver"
      >
        Tomar otra foto
      </button>

      <!-- HISTORIAL LOCAL DE QRs -->
      <div
        v-if="history.length"
        class="mt-4 border-top pt-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong class="small">
            Imágenes de esta sesión ({{ history.length }})
          </strong>
          <button
            class="btn btn-link btn-sm text-danger p-0"
            @click="clearHistory"
          >
            Borrar lista
          </button>
        </div>
        <div class="d-flex flex-wrap">
          <div
            v-for="item in history"
            :key="item.id"
            class="mr-2 mb-2 text-center"
          >
            <img
              :src="item.qrDataUrl"
              alt="QR"
              style="width: 72px; height: 72px;"
            >
            <div class="small text-muted">
              {{ item.specialty }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TOAST (Bootstrap nativo: el website no usa Vuetify) -->
    <div
      v-if="snackbar.show"
      class="event-toast"
      :class="`event-toast--${snackbar.color}`"
    >
      {{ snackbar.text }}
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import QRCode from 'qrcode';

const PUBLIC_CONFIG_QUERY = gql`
  query EventImagePublicConfig($eventCode: String!, $authCode: String!) {
    eventImagePublicConfig(eventCode: $eventCode, authCode: $authCode) {
      success
      code
      configId
      eventName
      specialties
      logoEventUrl
      logoAmiiUrl
      startDate
      endDate
    }
  }
`;

const PUBLIC_GENERATE_MUTATION = gql`
  mutation EventImagePublicGenerate(
    $eventCode: String!
    $authCode: String!
    $sourceUrl: String!
    $specialty: String
    $extraInfo: String
  ) {
    eventImagePublicGenerate(
      eventCode: $eventCode
      authCode: $authCode
      sourceUrl: $sourceUrl
      specialty: $specialty
      extraInfo: $extraInfo
    ) {
      success
      code
      imageId
      imageUrl
      shareUrl
    }
  }
`;

const HISTORY_KEY = 'amii_event_image_history';

export default {
  name: 'EventImagePublic',
  data() {
    return {
      step: 'codes',
      eventCode: '',
      authCode: '',
      eventName: '',
      specialties: [],
      selectedSpecialty: null,
      extraInfo: '',
      validating: false,
      capturing: false,
      errorMessage: '',
      resultImageUrl: '',
      resultShareUrl: '',
      qrDataUrl: '',
      history: [],
      snackbar: {show: false, text: '', color: 'success'},
      snackbarTimer: null,
      mediaStream: null,
    };
  },
  computed: {
    multimediaRoot() {
      return process.env.NODE_ENV === 'production'
        ? process.env.VUE_APP_MULTIMEDIA_HTTPS
        : process.env.VUE_APP_MULTIMEDIA_HTTP;
    },
    // Raíz del API (sin /images) para el endpoint público de subida.
    apiRoot() {
      const root = this.multimediaRoot || '';
      return root.replace(/\/images\/?$/, '');
    },
    // Raíz pública de las imágenes promocionales de evento.
    eventImagesRoot() {
      return `${this.apiRoot}/event-images/`;
    },
    whatsappShareUrl() {
      const text = encodeURIComponent(
          `Mira la imagen que generé con AMII es IA 🩺✨ ${this.resultShareUrl}`,
      );
      return `https://wa.me/?text=${text}`;
    },
  },
  mounted() {
    this.loadHistory();
  },
  beforeDestroy() {
    this.stopCamera();
    if (this.snackbarTimer) clearTimeout(this.snackbarTimer);
  },
  methods: {
    notify(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
      if (this.snackbarTimer) clearTimeout(this.snackbarTimer);
      this.snackbarTimer = setTimeout(() => {
        this.snackbar.show = false;
      }, 3500);
    },

    loadHistory() {
      try {
        const raw = localStorage.getItem(HISTORY_KEY);
        this.history = raw ? JSON.parse(raw) : [];
      } catch (err) {
        this.history = [];
      }
    },

    saveHistory() {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.history));
      } catch (err) {
        console.warn(
            '[EventImagePublic] No se pudo guardar el historial:', err,
        );
      }
    },

    clearHistory() {
      this.history = [];
      this.saveHistory();
      this.notify('Lista de imágenes borrada');
    },

    async validateCodes() {
      if (!this.eventCode || !this.authCode) {
        this.errorMessage = 'Ingresá ambos códigos.';
        return;
      }
      this.validating = true;
      this.errorMessage = '';
      try {
        const {data} = await this.$apollo.query({
          query: PUBLIC_CONFIG_QUERY,
          variables: {
            eventCode: this.eventCode.trim(),
            authCode: this.authCode.trim(),
          },
          fetchPolicy: 'no-cache',
        });
        const res = data.eventImagePublicConfig;
        if (!res || !res.success) {
          this.errorMessage = this.messageFromCode(res ? res.code : null);
          return;
        }
        this.eventName = res.eventName || 'Evento';
        this.specialties = res.specialties || [];
        this.step = 'capture';
        await this.$nextTick();
        await this.startCamera();
      } catch (err) {
        this.errorMessage = 'No se pudo validar el evento. Intentá de nuevo.';
      } finally {
        this.validating = false;
      }
    },

    messageFromCode(code) {
      switch (code) {
        case 'INVALID_EVENT_CODE':
          return 'El código del evento o de autorización no es válido.';
        case 'EVENT_OUT_OF_RANGE':
          return 'La herramienta no está habilitada en este momento '
            + 'para este evento.';
        case 'GENERATION_TIMEOUT':
          return 'La generación tardó más de lo esperado. '
            + 'Intentá de nuevo.';
        case 'GENERATION_UNAVAILABLE':
          return 'El servicio de generación no está disponible ahora. '
            + 'Avisá al equipo de AMII.';
        case 'SOURCE_IMAGE_NOT_FOUND':
          return 'No se encontró tu foto. Volvé a tomarla.';
        case 'GENERATION_FAILED':
          return 'No se pudo generar la imagen. Intentá de nuevo.';
        default:
          return 'No se pudo habilitar la herramienta.';
      }
    },

    async startCamera() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {facingMode: 'user'},
          audio: false,
        });
        const video = this.$refs.video;
        if (video) {
          video.srcObject = this.mediaStream;
        }
      } catch (err) {
        this.errorMessage = 'No se pudo acceder a la cámara. '
          + 'Verificá los permisos.';
      }
    },

    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
        this.mediaStream = null;
      }
    },

    async capturePhoto() {
      const video = this.$refs.video;
      if (!video) return;
      this.capturing = true;
      this.errorMessage = '';
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 720;
        canvas.height = video.videoHeight || 960;
        canvas.getContext('2d').drawImage(
            video, 0, 0, canvas.width, canvas.height,
        );
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

        this.step = 'waiting';
        this.stopCamera();

        const uploaded = await this.uploadPhoto(dataUrl);
        await this.generateImage(uploaded);
      } catch (err) {
        this.errorMessage = 'No se pudo generar la imagen. Intentá de nuevo.';
        this.step = 'capture';
        await this.startCamera();
      } finally {
        this.capturing = false;
      }
    },

    async uploadPhoto(dataUrl) {
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append('file', blob, 'capture.jpg');
      formData.append('eventCode', this.eventCode.trim());
      formData.append('authCode', this.authCode.trim());

      const base = this.apiRoot || '';
      const res = await fetch(`${base}/subir/evento`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('upload_failed');
      const json = await res.json();
      const created = json?.data?.imageCreate?.[0];
      if (!created || !created.url) throw new Error('upload_no_url');
      return `/event-images/${created.url}`;
    },

    async generateImage(sourceUrl) {
      const {data} = await this.$apollo.mutate({
        mutation: PUBLIC_GENERATE_MUTATION,
        variables: {
          eventCode: this.eventCode.trim(),
          authCode: this.authCode.trim(),
          sourceUrl,
          specialty: this.selectedSpecialty,
          extraInfo: this.extraInfo ? this.extraInfo.trim() : null,
        },
      });
      const res = data.eventImagePublicGenerate;
      if (!res || !res.success) {
        this.errorMessage = this.messageFromCode(res ? res.code : null);
        this.step = 'capture';
        await this.startCamera();
        return;
      }

      const cleanUrl = res.imageUrl.replace(/^\/event-images\//, '');
      this.resultImageUrl = `${this.eventImagesRoot}${cleanUrl}`;
      this.resultShareUrl = res.shareUrl;
      this.qrDataUrl = await QRCode.toDataURL(res.shareUrl, {
        width: 360,
        margin: 1,
        errorCorrectionLevel: 'H',
      });

      this.history.unshift({
        id: res.imageId,
        specialty: this.selectedSpecialty,
        shareUrl: res.shareUrl,
        qrDataUrl: this.qrDataUrl,
      });
      this.saveHistory();

      this.step = 'result';
    },

    startOver() {
      this.resultImageUrl = '';
      this.resultShareUrl = '';
      this.qrDataUrl = '';
      this.errorMessage = '';
      this.extraInfo = '';
      this.step = 'capture';
      this.$nextTick(() => this.startCamera());
    },
  },
};
</script>

<style scoped>
.event-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1080;
  padding: 12px 20px;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.event-toast--success {
  background-color: #28a745;
}

.event-toast--error {
  background-color: #dc3545;
}

.event-toast--warning {
  background-color: #ffc107;
  color: #212529;
}
</style>
